import { FileAddOutlined, FileTextOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/react";
import { Alert, Col, Drawer, Flex, Modal, Row, Splitter, Tabs, message } from "antd";
import React from "react";
import { useDispatch } from "react-redux";
import { Outlet } from "react-router-dom";
import { useNetworkState } from "react-use";
import Swal from "sweetalert2";
import ActiveRecordingView from "../../components/ActiveRecordingView/ActiveRecordingView.component";
import CreateClient from "../../components/CreateClient/CreateClient.component";
import { AITemplateModal } from "../../components/CreateNoteModal/AITemplateModal/AITemplateModal.component";
import CreateNoteModalGCS from "../../components/CreateNoteModal/CreateNoteModalGCS.component";
import CustomiseNote from "../../components/Customise/customise-note.component";
import RecordCTA from "../../components/RecordCTA/RecordCTA.component";
import Share from "../../components/Share/Share.modal";
import SuperdocComponent from "../../components/Superdoc/Superdoc.component";
import { NoteType } from "../../domain/notes.domain";
import { isLastPlusOneNote } from "../../domain/user.domain";
import { useAppSelector } from "../../hooks/redux-hooks";
import { useBreakpointView } from "../../hooks/ui-hook";
import useIntercomWithData from "../../hooks/useIntercomWithData";
import { getAllDcmCodes, getClientDetailsByUUID } from "../../service/client.service";
import {
  getCustomizationList,
  getNotesDetails,
  handleCancelRecording,
  uploadAudioFromIndexedDBViaSignedURL,
} from "../../service/notes.service";
import { resumableUploadManager } from "../../service/upload.service";
import {
  setAddClientView,
  setArtifactFocus,
  setAudioDevices,
  setCreateClientState,
  setCreateModalState,
  setCustomiseSectionState,
  setDemoState,
  setEditingState,
  setIsDemoPath,
  setLoadingNotesState,
  setNoteGenerating,
  setNotesViewVisible,
  setOpenShare,
  setSelectedClient,
  setSelectedNoteForEditing,
  setShowRecordingView,
  setSubmissionProgressState,
  setSuperdocLayout,
  setUploadProgressState,
  setUploadType,
} from "../../slices/appStateSlice";
import { resetClientForm, setClientForm, setCustomizationList, setDsm } from "../../slices/clientSlice";
import { setNotesDetails, setRecordingId } from "../../slices/notesSlice";
import {
  setIsPaused,
  setIsRecording,
  setMediaRecorder,
  setRecordingDetail,
  setRecordingDevice,
} from "../../slices/recordingsSlice";
import { setShowPaymentModal } from "../../slices/userSlice";
import type { AppDispatch } from "../../store";
import type { ArtifactKind, TNotes } from "../../types/index.type";
import { EventType, trackEvent } from "../../utils/analytics";
import { assembleBlob, clearBuffers, getChunkStats, purgeExpired } from "../../utils/audioStore";
import {
  MAX_RECORDING_TIME_SECONDS,
  PURGE_EXPIRED_AUDIO_FILES_DAYS,
  RECORDER_TIMESLICE_MS_GCS,
} from "../../utils/constants";
import { eventEmitter } from "../../utils/eventEmitter";
import { readSuperdocLayoutFromUrl } from "../../utils/layoutConfig";
import { fetchAudioInputDevices } from "../../utils/mediaRecorder.utils";
import { isTooShort, stopMediaRecorderAndStopTracks } from "../../utils/recording.utils";
import { releaseWakeLock, requestWakeLock } from "../../utils/wakeScreen";
import Sidebar from "./Sidebar.component";
import styles from "./home.module.scss";
import { checkMicrophoneMuteStatus } from "./utils";

// Safety net: clear the right-pane "regenerating" spinner if a version
// never reports done (e.g. generation failed — no status→done event fires).
const NOTE_GENERATING_TIMEOUT_MS = 3 * 60 * 1000;

const HomePage = () => {
  const componentName = "home";
  const dispatch = useDispatch<AppDispatch>();
  const isMobileThreePanView = useBreakpointView(1024);
  const isCompactView = useBreakpointView(1200);

  const {
    isCustomiseSectionVisible,
    uploadType,
    showRecordingView,
    demoState,
    selectedClient,
    isNotesViewVisible,
    noteGenerating,
    superdocLayout,
  } = useAppSelector((state) => state.appState);

  // Layout config (utils/layoutConfig): "notes-center" puts the notes list in
  // the center with Superdoc on the right; "superdoc-center" is the reverse.
  const notesCenter = superdocLayout === "notes-center";

  const { notesDetails } = useAppSelector((state) => state.notes);

  // Get the active client ID - prefer selectedClient (from client list), fallback to notesDetails (from selected note)
  const activeClientId = selectedClient?.id || notesDetails?.client || undefined;

  const networkState = useNetworkState();

  const { recordMimeType, mediaRecorder, isRecording, isPaused, recordingDetail, recordingDevice } = useAppSelector(
    (state) => state.recordings,
  );

  const { audioDevices } = useAppSelector((state) => state.appState);

  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const isSuperdocEnabled = userInfo.is_superdoc_enabled ?? false;

  // Determine customization modal title based on note type
  const customizeModalTitle = React.useMemo(() => {
    if (notesDetails?.note_type === NoteType.SOAP || notesDetails?.note_type === NoteType.DAP) {
      return "Add instructions to fix this note";
    }
    return "Add One-Time Instructions to fix this note";
  }, [notesDetails?.note_type]);
  const { bootWithData } = useIntercomWithData();

  const [recordingTime, setRecordingTime] = React.useState<number>(0);
  const captureRecordingIdRef = React.useRef("");
  const recordingInterval = React.useRef<NodeJS.Timeout | null>(null);
  const [isMicrophoneMuted, setIsMicrophoneMuted] = React.useState(false);
  const [isSuperdocExpanded, setIsSuperdocExpanded] = React.useState(false);

  // superdoc-center only: the right (notes) pane exists while a single note is
  // open OR a version is regenerating; otherwise it collapses and Superdoc
  // takes the full width. In notes-center the notes pane is always shown and
  // only the manual expand toggle collapses it.
  const rightPaneOpen = (isNotesViewVisible && !!notesDetails) || !!noteGenerating;
  const collapseRight = isSuperdocExpanded || !rightPaneOpen;

  // Superdoc's default split size differs per layout: ~36% as the right pane,
  // ~67% as the center pane. Each persists under its own localStorage key.
  const [defaultSuperdocSize] = React.useState<number | string>(() => {
    if (notesCenter) {
      const saved = localStorage.getItem("superdoc-right-pct");
      if (saved) {
        const pct = Number.parseFloat(saved);
        if (pct > 20 && pct < 60) return `${pct}%`;
      }
      return "36%";
    }
    const saved = localStorage.getItem("superdoc-panel-pct");
    if (saved) {
      const pct = Number.parseFloat(saved);
      if (pct > 10 && pct < 90) return `${pct}%`;
    }
    return "67%";
  });
  const createNoteModalRef = React.useRef<{
    cleanupAudioResources?: () => void;
    stopMicTracks?: () => void;
    resumeRecording?: () => Promise<void>;
    clearFileUploads?: () => void;
  } | null>(null);

  const populateStore = () => {
    getAllDcmCodes()
      .then((response) => {
        const dsm = response.data;
        dispatch(setDsm(dsm));
      })
      .catch((error) => {
        console.error("Error fetching DCM codes:", error);
      });
    getCustomizationList()
      .then((response) => {
        dispatch(setCustomizationList(response.data));
      })
      .catch((error) => {
        console.error("Error fetching customization list:", error);
      });

    // to silently purge expired audio files
    purgeExpired(PURGE_EXPIRED_AUDIO_FILES_DAYS);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: populateStore updates every render
  React.useEffect(() => {
    dispatch(setIsDemoPath(false));
    populateStore();
    bootWithData();
  }, [dispatch]);

  // Honor a ?layout= override on (client-side) navigation, not just full loads.
  React.useEffect(() => {
    const fromUrl = readSuperdocLayoutFromUrl();
    if (fromUrl && fromUrl !== superdocLayout) {
      dispatch(setSuperdocLayout(fromUrl));
    }
  }, [dispatch, superdocLayout]);

  const resetRecordingState = (withoutRefresh = false) => {
    if (mediaRecorder?.state !== "inactive") {
      stopMediaRecorderAndStopTracks(mediaRecorder);
    }

    // Stop all mic tracks first (including orphaned ones in the registry)
    createNoteModalRef.current?.stopMicTracks?.();

    // Cleanup audio resources in CreateNoteModal if available
    if (createNoteModalRef.current?.cleanupAudioResources) {
      createNoteModalRef.current.cleanupAudioResources();
    }

    // Clear file uploads (audio and PDFs) in CreateNoteModal if available
    if (createNoteModalRef.current?.clearFileUploads) {
      createNoteModalRef.current.clearFileUploads();
    }

    dispatch(setShowRecordingView(false));
    dispatch(setSubmissionProgressState(false));
    dispatch(setMediaRecorder(null));
    if (recordingInterval.current) {
      clearInterval(recordingInterval.current);
      recordingInterval.current = null;
    }
    setRecordingTime(0);
    dispatch(setUploadType(0));
    captureRecordingIdRef.current = "";
    dispatch(
      setRecordingDetail({
        title: "",
        note_type: undefined,
        category_type: 0,
        modality_type: "audio",
        gender_type: 0,
        language_type: 0,
        client_id: null,
        current_recording_id: null,
        fileList: [],
      }),
    );
    dispatch(resetClientForm());
    dispatch(setAddClientView(false));
    dispatch(setCreateModalState(false));
    dispatch(setEditingState(false));
    dispatch(setSelectedNoteForEditing(null));
    dispatch(setIsRecording(false));
    dispatch(setIsPaused(false));
    dispatch(setRecordingDevice(null));
    if (!withoutRefresh) {
      eventEmitter.emit("refreshNotes");
    }
    dispatch(setUploadProgressState(false));
  };

  const displayRecordingTimer = () => {
    // Start a timer to track recording time
    recordingInterval.current = setInterval(() => {
      if (recordingTime >= MAX_RECORDING_TIME_SECONDS) {
        return recordingTime;
      }
      setRecordingTime((time) => time + 1);
    }, 1000);
  };

  React.useEffect(() => {
    if (isRecording && recordingTime >= MAX_RECORDING_TIME_SECONDS) {
      handlePauseOrEndRecording();
      // handleSubmitAudio(); // We should not submit audio here, it will be submitted when the user clicks the submit button
    }
  }, [isRecording, recordingTime]);

  // ── Artifact auto-open: slide the notes panel open to a freshly
  // generated/updated artifact. Two triggers:
  //   * "noteUpdated" — a session finished processing (useAudioStatusCheck
  //     polling saw status flip to done)
  //   * "artifactUpdated" — the Superdoc agent changed the note/codes via
  //     tools (artifact_updated SSE widget)
  const showRecordingViewRef = React.useRef(showRecordingView);
  showRecordingViewRef.current = showRecordingView;
  const selectedClientRef = React.useRef(selectedClient);
  selectedClientRef.current = selectedClient;
  const artifactDebounceRef = React.useRef<NodeJS.Timeout | null>(null);
  const pendingArtifactRef = React.useRef<{ audioFileId: number; kinds: string[] } | null>(null);
  const generatingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    const refreshSelectedClient = () => {
      const client = selectedClientRef.current;
      if (!client?.uuid) return;
      getClientDetailsByUUID(client.uuid)
        .then((response) => dispatch(setSelectedClient(response.data)))
        .catch((error) => console.error("Error refreshing client details:", error));
    };

    const focusArtifact = (audioFileId: number, kind: ArtifactKind) => {
      dispatch(setNotesViewVisible(true));
      setIsSuperdocExpanded(false);
      dispatch(setArtifactFocus({ audioFileId, kind, ts: Date.now() }));
    };

    const handleNoteUpdated = (...args: unknown[]) => {
      const note = args[0] as TNotes;
      if (!note?.id) return;
      // Don't steal focus while the user is recording, or when the
      // finished note belongs to a different patient than the one open.
      if (showRecordingViewRef.current) return;
      if (note.client && note.client !== selectedClientRef.current?.id) return;
      // A regenerating version finished — clear the spinner state so the
      // pane swaps from the spinner to the note.
      dispatch(setNoteGenerating(null));
      dispatch(setNotesDetails(note));
      dispatch(setRecordingId(note.id));
      focusArtifact(note.id, "note");
      // Pull the client's regenerated progress summary (async on the
      // backend — one refetch, accept brief staleness).
      refreshSelectedClient();
    };

    const handleArtifactUpdated = (...args: unknown[]) => {
      const data = args[0] as { audio_file_id?: number; kinds?: string[]; generating?: boolean };
      if (!data?.audio_file_id) return;
      // A NEW note version is still regenerating — show the spinner state
      // in the right pane immediately, refresh the timeline so the
      // processing entry appears + polls, and let "noteUpdated" swap in
      // the finished note. A safety timeout clears the spinner if the
      // version fails (no status→done event ever fires in that case).
      if (data.generating) {
        if (showRecordingViewRef.current) return;
        dispatch(setNoteGenerating({ audioFileId: data.audio_file_id }));
        dispatch(setNotesViewVisible(true));
        setIsSuperdocExpanded(false);
        eventEmitter.emit("refreshNotes");
        if (generatingTimeoutRef.current) clearTimeout(generatingTimeoutRef.current);
        generatingTimeoutRef.current = setTimeout(() => {
          dispatch(setNoteGenerating(null));
        }, NOTE_GENERATING_TIMEOUT_MS);
        return;
      }
      // Any completed artifact update also clears a lingering spinner.
      dispatch(setNoteGenerating(null));
      const prev = pendingArtifactRef.current;
      pendingArtifactRef.current = {
        audioFileId: data.audio_file_id,
        kinds: Array.from(
          new Set([...(prev?.audioFileId === data.audio_file_id ? prev.kinds : []), ...(data.kinds || [])]),
        ),
      };
      // A single chat turn can emit several artifact widgets (note +
      // codes); debounce into one refetch + focus.
      if (artifactDebounceRef.current) clearTimeout(artifactDebounceRef.current);
      artifactDebounceRef.current = setTimeout(async () => {
        const pending = pendingArtifactRef.current;
        pendingArtifactRef.current = null;
        if (!pending) return;
        try {
          const response = await getNotesDetails(pending.audioFileId);
          dispatch(setNotesDetails(response.data));
          dispatch(setRecordingId(pending.audioFileId));
          dispatch(setLoadingNotesState(false));
          focusArtifact(pending.audioFileId, (pending.kinds[0] as ArtifactKind) || "note");
          if (pending.kinds.includes("progress_summary")) refreshSelectedClient();
          // Completed synchronous versions (code edits) are brand-new
          // notes — refresh the list so they appear in the timeline.
          eventEmitter.emit("refreshNotes");
        } catch (error) {
          console.error("Error refreshing note after artifact update:", error);
        }
      }, 300);
    };

    eventEmitter.on("noteUpdated", handleNoteUpdated);
    eventEmitter.on("artifactUpdated", handleArtifactUpdated);
    return () => {
      eventEmitter.off("noteUpdated", handleNoteUpdated);
      eventEmitter.off("artifactUpdated", handleArtifactUpdated);
      if (artifactDebounceRef.current) clearTimeout(artifactDebounceRef.current);
      if (generatingTimeoutRef.current) clearTimeout(generatingTimeoutRef.current);
    };
  }, [dispatch]);

  // Listen for resume recording events from notes list
  React.useEffect(() => {
    const handleResumeEvent = async (...args: unknown[]) => {
      const data = args[0] as { audioId: number; startTime: number };
      console.log(`[RESUME] Resuming recording for audioId: ${data.audioId} at ${data.startTime}s`);

      // Set the recording time to continue from where it left off
      setRecordingTime(data.startTime);
      const devices = await fetchAudioInputDevices();
      dispatch(setAudioDevices(devices));
      dispatch(setRecordingDevice(devices[0]));

      // Set the audio ID
      captureRecordingIdRef.current = data.audioId.toString();

      // Call the CreateNoteModalGCS resume method via ref
      if (createNoteModalRef.current?.resumeRecording) {
        await createNoteModalRef.current.resumeRecording();
      } else {
        console.error("[RESUME] CreateNoteModalGCS resumeRecording method not available");
      }
    };

    eventEmitter.on("resumeRecording", handleResumeEvent);

    return () => {
      eventEmitter.off("resumeRecording", handleResumeEvent);
    };
  }, [dispatch]);

  const handlePauseOrEndRecording = async () => {
    await releaseWakeLock();
    dispatch(setIsRecording(false));
    dispatch(setIsPaused(true));
    if (recordingInterval.current) {
      clearInterval(recordingInterval.current);
      recordingInterval.current = null;
    }

    trackEvent(EventType.STOP_BUTTON, getCaptureDetails());
    if (mediaRecorder?.state === "inactive") {
      return;
    }
    mediaRecorder?.pause();
  };

  const handleResumeRecording = async () => {
    if (!networkState.online) {
      Swal.fire({
        icon: "error",
        title: "Unable to Resume Session",
        text: "It looks like you're currently offline. Make sure your device is connected to Wi-Fi or mobile data before resuming your session.",
      });
      return;
    }

    // Log pipeline health before resume to help diagnose post-interruption issues
    const tracks = mediaRecorder?.stream?.getAudioTracks() ?? [];
    const track = tracks.length > 0 ? tracks[0] : null;
    console.log("[RESUME] Pipeline state before resume:", {
      recorderState: mediaRecorder?.state,
      streamActive: mediaRecorder?.stream?.active,
      trackReadyState: track?.readyState,
      trackEnabled: track?.enabled,
      trackMuted: track?.muted,
      trackLabel: track?.label,
    });

    await requestWakeLock();
    dispatch(setIsPaused(false));
    displayRecordingTimer();
    if (mediaRecorder?.state === "paused") {
      mediaRecorder?.resume();
    } else {
      mediaRecorder?.start(RECORDER_TIMESLICE_MS_GCS);
    }
    dispatch(setIsRecording(true));
    trackEvent(EventType.RESUME_BUTTON, getCaptureDetails());
  };

  const getCaptureDetails = () => {
    const tracks = mediaRecorder?.stream?.getAudioTracks();
    const track = tracks?.length ? tracks[0] : null;
    return {
      modalityMode: recordingDetail.modality_type,
      audioDevices: audioDevices,
      selectedDevice: recordingDevice,
      mediaRecorder: {
        state: mediaRecorder?.state,
        streamActive: mediaRecorder?.stream?.active,
        trackMuted: track ? track.muted : null,
        trackEnabled: track ? track.enabled : null,
        trackReadyState: track ? track.readyState : null,
        mimeType: mediaRecorder?.mimeType,
      },
      isRecording: isRecording,
      audioFileId: captureRecordingIdRef.current || resumableUploadManager.getAudioId()?.toString(),
      hasActiveResumableSession: resumableUploadManager.hasActiveSession(),
    };
  };

  const handleSubmitAudio = () => {
    if (!networkState.online) {
      Swal.fire({
        icon: "error",
        title: "Unable to Submit Recording",
        text: "It looks like you're currently offline. Make sure your device is connected to Wi-Fi or mobile data before submitting your recording.",
      });
      Sentry.captureException(new Error("Unable to submit audio because of offline"), {
        extra: getCaptureDetails(),
      });
      return;
    }
    try {
      if (isTooShort(recordingTime)) {
        Swal.fire({
          icon: "error",
          title: "Recording Too Short",
          text: "This recording is too short. Please record for at least 15 seconds, then try again.",
        });
        return;
      }

      if (isLastPlusOneNote(userInfo)) {
        dispatch(setShowPaymentModal(true));
      }

      dispatch(setSubmissionProgressState(true));
      trackEvent(EventType.SUBMIT_AUDIO_MODAL, getCaptureDetails());

      completeSubmissionGCS(isLastPlusOneNote(userInfo));

      // non blocking upload of audio from indexedDB
      if (captureRecordingIdRef.current) {
        uploadAudioFromIndexedDB(captureRecordingIdRef.current.toString());
      }
    } catch (error) {
      dispatch(setSubmissionProgressState(false));
      Sentry.captureException(error, {
        extra: getCaptureDetails(),
      });
      Swal.fire({
        icon: "error",
        title: "Error submitting audio",
        text: "Please try again or contact support.",
      });
    }
  };

  React.useEffect(() => {
    const isAfterPayment = localStorage.getItem("isPaymentSuccess");
    if (isAfterPayment) {
      dispatch(setOpenShare(true));
      localStorage.removeItem("isPaymentSuccess");
    }
    return () => {
      releaseWakeLock();
    };
  }, []);

  /**
   * If media Recorder is not null, add an error event listener to it
   */
  React.useEffect(() => {
    if (!mediaRecorder) {
      return;
    }
    const errorHandler = (event: any) => {
      Sentry.captureException(event.error);
    };

    mediaRecorder.addEventListener("error", errorHandler);
    return () => {
      mediaRecorder.removeEventListener("error", errorHandler);
    };
  }, [mediaRecorder]);

  const uploadAudioFromIndexedDB = async (audio_id: string) => {
    try {
      const completeAudio = await assembleBlob(audio_id);
      if (!completeAudio) {
        return;
      }
      const contentType = recordMimeType || "audio/webm";
      await uploadAudioFromIndexedDBViaSignedURL(audio_id, completeAudio, contentType);
    } catch (error) {
      const chunkStats = await getChunkStats(audio_id);
      Sentry.captureException(error, {
        extra: {
          audio_id,
          chunkStats,
          error,
          flow: "signed_url_backup_upload",
        },
      });
      // Silent failure - backup upload is not critical
    }
  };

  const completeSubmissionGCS = async (isLocked = false) => {
    if (uploadType === 0 && resumableUploadManager.hasActiveSession()) {
      console.log("Finalizing resumable upload submission");
      try {
        // Stop MediaRecorder first to prevent late chunks from arriving during finalization
        if (mediaRecorder?.state !== "inactive") {
          console.log("Stopping MediaRecorder before finalization");
          stopMediaRecorderAndStopTracks(mediaRecorder);
        }

        // Release mic hardware immediately (fire-and-forget, does not block submission)
        createNoteModalRef.current?.stopMicTracks?.();

        // Wait briefly for any final chunks to be processed
        await new Promise((resolve) => setTimeout(resolve, 1000));

        await resumableUploadManager.finalizeUpload(isLocked);

        trackEvent(EventType.SUBMIT_AUDIO_FINAL, {
          audioFileId: resumableUploadManager.getAudioId()?.toString(),
          modalityMode: recordingDetail.modality_type,
          uploadMethod: "resumable",
        });
        message.success("Session submitted successfully. Processing in background...");
      } catch (error) {
        console.error("Error finalizing resumable upload:", error);
        Sentry.captureException(error, { extra: getCaptureDetails() });
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: "We couldn't finalize the submission. Please click on process now button to proceed.",
        });
        dispatch(setSubmissionProgressState(false));
      } finally {
        resetRecordingState();
      }
    } else if (uploadType === 0 && !resumableUploadManager.hasActiveSession()) {
      console.error("Attempted to complete submission but no active resumable session found.");
      Sentry.captureMessage("Submission attempt without active resumable session", {
        extra: getCaptureDetails(),
        level: "warning",
      });
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: "We couldn't finalize the submission. Please click on process now button to proceed.",
      });
      dispatch(setSubmissionProgressState(false));
    } else {
      console.warn("completeSubmission called for unexpected uploadType:", uploadType);
      if (uploadType === 0) resetRecordingState();
    }
  };

  React.useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        console.log("Page is now in the foreground!");
        await requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleCancel = async () => {
    // This fires when the recording gets cancelled due to any errors in connection or media recorder
    const audioIdToCancel = captureRecordingIdRef.current || resumableUploadManager.getAudioId()?.toString();

    if (audioIdToCancel) {
      try {
        await handleCancelRecording({ audio_id: audioIdToCancel, status: "cancel" });
        await resumableUploadManager.cancelGcsUpload();
        try {
          await clearBuffers(captureRecordingIdRef.current.toString());
        } catch (error) {
          console.error("Error clearing buffers:", error, "audio_id", captureRecordingIdRef.current);
          // pass
        }
      } catch (error) {
        console.error("Error notifying backend of cancellation via handleRetry:", error);
        Sentry.captureException(error, { extra: { audio_id: audioIdToCancel } });
      }
    } else {
      console.warn("handleRetry called without an apparent audio ID to cancel.");
    }
    resetRecordingState();
  };

  const cancelRecording = async () => {
    // This fires when the user clicks the cancel button in the recording view
    Swal.fire({
      title: "Are you sure you want to cancel?",
      text: "You will lose the entire recording, including any previously recorded parts, if you cancel.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel it!",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        trackEvent(EventType.CANCEL_RECORDING, {
          recordingId: captureRecordingIdRef.current,
          modalityType: recordingDetail.modality_type,
          recordingTime: recordingTime,
          isPaused: isPaused,
        });
        handleCancel();
      }
    });
  };

  const items = [
    {
      key: "2",
      label: "Record",
      children: (
        <>
          {isMicrophoneMuted && (
            <Alert message="Microphone is muted. Please unmute to record properly." banner closable />
          )}
          <Col span={24} md={24} xl={24} className={styles[`${componentName}__container-right`]}>
            <Flex vertical align="center">
              {showRecordingView ? (
                <ActiveRecordingView
                  isPaused={isPaused}
                  recordingTime={recordingTime}
                  handlePauseOrEndRecording={handlePauseOrEndRecording}
                  handleResumeRecording={handleResumeRecording}
                  handleSubmitAudio={handleSubmitAudio}
                  cancelRecording={cancelRecording}
                />
              ) : (
                <RecordCTA />
              )}
            </Flex>
          </Col>
        </>
      ),
      icon: <FileAddOutlined />,
    },
    {
      key: "1",
      label: "Notes",
      children: (
        <Col span={24} md={24} className={styles[`${componentName}__container-left`]} id="notes-container-mobile">
          <Outlet />
        </Col>
      ),
      icon: <FileTextOutlined />,
    },
    ...(isSuperdocEnabled
      ? [
          {
            key: "3",
            label: "SuperDoc",
            children: (
              <SuperdocComponent
                key={activeClientId ?? "no-client"}
                clientId={activeClientId}
                style={{ height: "calc(100dvh - 64px - 57px)", width: "100vw" }}
              />
            ),
            icon: (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6.9275 12.0033L6.25 14.375L5.5725 12.0033C5.39739 11.3907 5.06908 10.8328 4.61855 10.3823C4.16802 9.93175 3.61012 9.60344 2.9975 9.42833L0.625 8.75L2.99667 8.0725C3.60928 7.89739 4.16718 7.56908 4.61772 7.11855C5.06825 6.66802 5.39656 6.11012 5.57167 5.4975L6.25 3.125L6.9275 5.49667C7.10261 6.10928 7.43092 6.66718 7.88145 7.11772C8.33198 7.56825 8.88988 7.89656 9.5025 8.07167L11.875 8.75L9.50333 9.4275C8.89072 9.60261 8.33282 9.93092 7.88228 10.3815C7.43175 10.832 7.10344 11.3899 6.92833 12.0025L6.9275 12.0033ZM13.9658 6.0125L13.75 6.875L13.5342 6.0125C13.4106 5.51799 13.155 5.06635 12.7947 4.70586C12.4343 4.34537 11.9828 4.08958 11.4883 3.96583L10.625 3.75L11.4883 3.53417C11.9828 3.41042 12.4343 3.15463 12.7947 2.79414C13.155 2.43365 13.4106 1.98201 13.5342 1.4875L13.75 0.625L13.9658 1.4875C14.0894 1.98211 14.3452 2.43382 14.7057 2.79432C15.0662 3.15482 15.5179 3.41056 16.0125 3.53417L16.875 3.75L16.0125 3.96583C15.5179 4.08944 15.0662 4.34518 14.7057 4.70568C14.3452 5.06618 14.0894 5.51789 13.9658 6.0125ZM12.8283 15.8892L12.5 16.875L12.1717 15.8892C12.0796 15.613 11.9245 15.3621 11.7187 15.1563C11.5129 14.9505 11.262 14.7954 10.9858 14.7033L10 14.375L10.9858 14.0467C11.262 13.9546 11.5129 13.7995 11.7187 13.5937C11.9245 13.3879 12.0796 13.137 12.1717 12.8608L12.5 11.875L12.8283 12.8608C12.9204 13.137 13.0755 13.3879 13.2813 13.5937C13.4871 13.7995 13.738 13.9546 14.0142 14.0467L15 14.375L14.0142 14.7033C13.738 14.7954 13.4871 14.9505 13.2813 15.1563C13.0755 15.3621 12.9204 15.613 12.8283 15.8892Z"
                  stroke="currentColor"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ),
          },
        ]
      : []),
  ];

  React.useEffect(() => {
    const { isNoteModalOpen, isClientModalOpen, recordingDetails, clientForm } = demoState;

    if (isNoteModalOpen || isClientModalOpen) {
      if (isNoteModalOpen) {
        dispatch(setRecordingDetail(recordingDetails));
        dispatch(setCreateModalState(true));
      }
      if (isClientModalOpen) {
        dispatch(setCreateClientState(true));
      }
      if (clientForm) {
        dispatch(setAddClientView(true));
        dispatch(setClientForm(clientForm));
      }
      dispatch(
        setDemoState({
          isNoteModalOpen: false,
          recordingDetails: null,
          clientForm: null,
          isClientModalOpen: false,
        }),
      );
    }
  }, [demoState, dispatch]);

  // Shared panes for both layouts (notes-center / superdoc-center). The notes
  // pane is the routed <Outlet/>; Superdoc's expand/collapse semantics differ
  // per layout, so they're passed in.
  const notesOutlet = (extraStyle?: React.CSSProperties) => (
    <div
      className={styles[`${componentName}__container-left`]}
      id="notes-container"
      style={{ borderRadius: !isSuperdocEnabled ? "0 !important" : undefined, ...extraStyle }}
    >
      <Outlet />
    </div>
  );

  const renderSuperdoc = (opts: { expanded: boolean; canCollapse: boolean; style?: React.CSSProperties }) => (
    <SuperdocComponent
      key={activeClientId ?? "no-client"}
      clientId={activeClientId}
      className={styles[`${componentName}__superdoc`]}
      expanded={opts.expanded}
      onExpand={() => setIsSuperdocExpanded(true)}
      onCollapse={() => setIsSuperdocExpanded(false)}
      canCollapse={opts.canCollapse}
      style={opts.style}
    />
  );

  const splitterStyle: React.CSSProperties = {
    background: "transparent",
    flex: 1,
    minWidth: 0,
    height: isMobileThreePanView ? "calc(100dvh - 64px)" : "100dvh",
  };

  return (
    <>
      {isMicrophoneMuted && <Alert message="Microphone is muted. Please unmute to record properly." banner closable />}
      <Row wrap={false} className={styles[`${componentName}__container`]}>
        <Sidebar
          isPaused={isPaused}
          recordingTime={recordingTime}
          handlePauseOrEndRecording={handlePauseOrEndRecording}
          handleResumeRecording={handleResumeRecording}
          handleSubmitAudio={handleSubmitAudio}
          cancelRecording={cancelRecording}
        />
        {isSuperdocEnabled && !isCompactView ? (
          notesCenter ? (
            // Notes list front-and-center; Superdoc in the right pane.
            <Splitter
              style={splitterStyle}
              onResizeEnd={(sizes) => {
                // sizes[1] is the Superdoc (right) panel. Persist its width %.
                if (sizes[1] > 20 && sizes[1] < 60) {
                  localStorage.setItem("superdoc-right-pct", String(sizes[1]));
                }
              }}
            >
              <Splitter.Panel size={isSuperdocExpanded ? 0 : undefined} min={isSuperdocExpanded ? 0 : "30%"}>
                {notesOutlet()}
              </Splitter.Panel>
              <Splitter.Panel
                defaultSize={defaultSuperdocSize}
                min={isSuperdocExpanded ? undefined : "33%"}
                max={isSuperdocExpanded ? undefined : "50%"}
                size={isSuperdocExpanded ? "100%" : undefined}
              >
                {renderSuperdoc({ expanded: isSuperdocExpanded, canCollapse: true })}
              </Splitter.Panel>
            </Splitter>
          ) : (
            // Superdoc front-and-center; single-note view in the right pane.
            <Splitter
              style={splitterStyle}
              onResizeEnd={(sizes) => {
                // sizes[0] is the Superdoc (center) panel. Persist its width %.
                if (sizes[0] > 10 && sizes[0] < 90) {
                  localStorage.setItem("superdoc-panel-pct", String(sizes[0]));
                }
              }}
            >
              <Splitter.Panel
                defaultSize={defaultSuperdocSize}
                min={collapseRight ? undefined : "50%"}
                max={collapseRight ? undefined : "70%"}
                size={collapseRight ? "100%" : undefined}
              >
                {renderSuperdoc({ expanded: collapseRight, canCollapse: rightPaneOpen })}
              </Splitter.Panel>
              <Splitter.Panel size={collapseRight ? 0 : undefined} min={collapseRight ? 0 : "30%"}>
                {notesOutlet()}
              </Splitter.Panel>
            </Splitter>
          )
        ) : isSuperdocEnabled && isCompactView ? (
          notesCenter ? (
            <Flex style={{ flex: 1, minWidth: 0, height: isMobileThreePanView ? "calc(100dvh - 64px)" : "100dvh" }}>
              {!isSuperdocExpanded && notesOutlet({ flex: 1, minWidth: 0, height: "100%" })}
              {renderSuperdoc({
                expanded: isSuperdocExpanded,
                canCollapse: true,
                style: {
                  width: isSuperdocExpanded ? "100%" : 368,
                  minWidth: isSuperdocExpanded ? undefined : 368,
                  height: "100%",
                },
              })}
            </Flex>
          ) : (
            <Flex style={{ flex: 1, minWidth: 0, height: isMobileThreePanView ? "calc(100dvh - 64px)" : "100dvh" }}>
              {renderSuperdoc({
                expanded: collapseRight,
                canCollapse: rightPaneOpen,
                style: {
                  width: collapseRight ? "100%" : 368,
                  minWidth: collapseRight ? undefined : 368,
                  height: "100%",
                },
              })}
              {!collapseRight && notesOutlet({ flex: 1, minWidth: 0, height: "100%" })}
            </Flex>
          )
        ) : (
          notesOutlet({
            flex: 1,
            minWidth: 0,
            height: isMobileThreePanView ? "calc(100dvh - 64px)" : "100dvh",
          })
        )}
        <CreateClient />
        <CreateNoteModalGCS
          isCaptureEnabled={true}
          captureRecordingIdRef={captureRecordingIdRef}
          resetRecordingState={resetRecordingState}
          handleCancel={handleCancel}
          setRecordingTime={setRecordingTime}
          displayRecordingTimer={displayRecordingTimer}
          checkMicrophoneMuteStatus={(stream: MediaStream) => checkMicrophoneMuteStatus(stream, setIsMicrophoneMuted)}
          getCaptureDetails={getCaptureDetails}
          recordingTime={recordingTime}
          ref={createNoteModalRef}
        />
        <AITemplateModal />
        <Share />
      </Row>
      {isMobileThreePanView ? (
        <Drawer
          title={customizeModalTitle}
          open={isCustomiseSectionVisible}
          placement="bottom"
          height="85%"
          closable={true}
          destroyOnClose
          onClose={() => dispatch(setCustomiseSectionState(false))}
          width="100%"
        >
          <Flex
            vertical
            justify="space-between"
            style={{
              height: "100%",
            }}
          >
            {notesDetails && (notesDetails.note_type === 0 || notesDetails.note_type === 2) && (
              <Flex className={`${styles[`${componentName}__customise_clearance`]}`}>
                <span />
              </Flex>
            )}
            <CustomiseNote noPadding={true} />
          </Flex>
        </Drawer>
      ) : (
        <Modal
          title={customizeModalTitle}
          open={isCustomiseSectionVisible}
          onCancel={() => dispatch(setCustomiseSectionState(false))}
          footer={null}
          width={450}
          destroyOnClose
          zIndex={999}
          styles={{
            body: {
              height: "600px",
              maxHeight: "80vh",
              overflowY: "auto",
            },
          }}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            top: "auto",
            margin: 0,
            paddingBottom: 0,
          }}
        >
          <Flex
            vertical
            justify="space-between"
            style={{
              height: "100%",
            }}
          >
            {notesDetails && (notesDetails.note_type === 0 || notesDetails.note_type === 2) && (
              <Flex className={`${styles[`${componentName}__customise_clearance`]}`}>
                <span />
              </Flex>
            )}
            <CustomiseNote noPadding={true} />
          </Flex>
        </Modal>
      )}
      <Tabs
        destroyInactiveTabPane
        tabPosition={"bottom"}
        size="large"
        items={items}
        className={`${styles[`${componentName}__mobile_view_tabs`]}`}
        centered
        onChange={(key) => {
          if (key === "2" || key === "3") {
            dispatch(setNotesViewVisible(false));
          }
        }}
      />
    </>
  );
};

export default HomePage;
